import { css } from '@emotion/react';
import {
  ActionList,
  Badge,
  Button,
  color,
  Icon,
  Input,
  MediaCard,
  Modal,
  Popover,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import CreateProject from '@pages/home/sections/components/CreateProject';
import AiLoadingThumbnail from '@pages/home/components/AiLoadingThumbnail';
import { AxiosError } from 'axios';
import { onMobile } from '@/styles/responsive';
import {
  AlignCenterFlex,
  CardShadowStyle,
  ColumnFlex,
  JustifyCenterFlex,
  MarginTop10,
  MarginZero,
  TextAlignCenter
} from '@/styles';
import {
  useDebounce,
  useDidUpdateEffect,
  useIsAdminSelector,
  useIsMobile,
  useIsOwner,
  useModalState,
  useRequest,
  useUser,
  useDynamicMutation,
  useChannelLimit,
  usePromotionModal
} from '@/hooks';
import { keyPressHandler, toast, badgeProps } from '@/utils';
import LoadingSpinner from '@/components/LoadingSpinner';
import { ClickEventName, InputEventName, PromotionTriggerLocation, track } from '@/track';
import { STATION_URL } from '@/env';
import { coreApi } from '@/api';
import { authApiWithToken } from '@/apis/auth';
import CreateProjectTemplateModal from './components/CreateProjectTemplateModal';
import AIFailedTumbnail from '../components/AIFailedTumbnail';
import DeleteProjectModal from './components/DeleteProjectModal';
import DisableCreateProject from './components/DisableCreateProject';
import { usePlanLimits } from '@/hooks/usePlanLimits';

type ProjectWithTranslationProgress = Project & {
  translationProgress?: TranslationProgressResponseModel;
  isOptimisticTranslation?: boolean;
};

function ProjectList() {
  const { appId } = useParams<AppParam>();
  const { modal, openModal, closeModal } = useModalState();
  const { checkLimitAndShowModal, modal: limitModal } = useChannelLimit();
  const { openPromotionModal: openPromotionModalHook } = usePromotionModal();

  const isMobile = useIsMobile();
  const history = useHistory();

  const { data: user } = useUser();
  const { data: isAdmin } = useIsAdminSelector();
  const { data: isOwner } = useIsOwner();
  const { data: creditValues } = useRequest<CommonResponseModel<RemainCreditInfoResponseModel>>(
    `/apps/${appId}/credit`
  );

  const { hasReached } = usePlanLimits();

  const checkPermissionForCreateProject = React.useCallback(
    (event: React.MouseEvent) => {
      if (user?.level !== 'Owner' && user?.level !== 'Author' && user?.level !== 'Developer') {
        event.preventDefault();
        openPermissionModal();

        return false;
      }

      return true;
    },
    [user, openPermissionModal]
  );

  const axiosConfig = {
    baseUrl: 'https://realworld.to',
    headers: {
      'X-Rwd-Api-Version': '1.1',
      from: 'home'
    }
  };

  const {
    data: { data: projects } = {},
    error,
    mutate: mutateProjects
  } = useRequest<CommonResponseModel<Project[]>>(`/apps/${appId}/projects`, {}, axiosConfig);

  const { data: { data: applicationInfo } = {}, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);

  const hasReachedLimit = applicationInfo?.projectCreationLimit
    ? applicationInfo.projectCreationLimit <= applicationInfo?.createdProjectCount
    : hasReached('projects', applicationInfo?.createdProjectCount ?? 0);

  const { plan } = usePlanLimits();
  const userType = (user as { userType?: string | number } | undefined)?.userType;
  const canDuplicateProjects = canDuplicateProject(user?.level, isAdmin);
  const canTranslateProjects = canUseProjectTranslation(
    appId,
    isAdmin,
    user?.level,
    user?.email,
    userType
  );

  // 동적 mutation hook 추가
  const { mutateByPath, isLoading: isDeletingProject } = useDynamicMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const [newProjectId, setNewProjectId] = useState<string | null>(null);
  const [translatingProjectId, setTranslatingProjectId] = useState<string | null>(null);
  const [optimisticTranslationProjects, setOptimisticTranslationProjects] = useState<
    ProjectWithTranslationProgress[]
  >([]);
  const [translationProgressByProjectId, setTranslationProgressByProjectId] = useState<
    Record<string, TranslationProgressResponseModel>
  >({});
  const terminalTranslationToastProjectIdsRef = useRef<Set<string>>(new Set());
  const unavailableProgressProjectIdsRef = useRef<Set<string>>(new Set());
  const newProjectRef = useRef<HTMLDivElement>(null);

  useDidUpdateEffect(() => {
    setSearchQuery('');
  }, [appId]);

  useEffect(() => {
    if (newProjectId && newProjectRef.current) {
      newProjectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setNewProjectId(null), 1500);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [newProjectId, projects]);

  const allProjects = useMemo<ProjectWithTranslationProgress[] | undefined>(() => {
    if (!projects) return undefined;

    const projectIds = new Set(projects.map((project) => project.id));
    const optimisticProjects = optimisticTranslationProjects
      .filter((project) => !projectIds.has(project.id))
      .map((project) => ({
        ...project,
        translationProgress: translationProgressByProjectId[project.id] ?? project.translationProgress
      }));
    const projectsWithProgress = projects.map((project) => ({
      ...project,
      translationProgress: translationProgressByProjectId[project.id]
    }));

    return [...optimisticProjects, ...projectsWithProgress];
  }, [projects, optimisticTranslationProjects, translationProgressByProjectId]);

  const projectsWithQuery = useMemo(() => {
    if (!allProjects) return undefined;

    const filtered = allProjects?.filter((project) =>
      project.name?.toLowerCase().includes(debouncedSearchQuery.trimStart().trimEnd().toLowerCase())
    );

    return filtered;
  }, [allProjects, debouncedSearchQuery, appId]);

  const visibleTranslationPollingProjectIds = useMemo(
    () =>
      projectsWithQuery
        ?.filter((project) => {
          const progress = project.translationProgress ?? translationProgressByProjectId[project.id];
          return (
            isTranslationProject(project) &&
            (isActiveTranslationProgress(progress) ||
              (!progress && project.status === 'Creating' && isTranslationProject(project)))
          );
        })
        .map((project) => project.id) ?? [],
    [projectsWithQuery, translationProgressByProjectId]
  );
  const visibleTranslationPollingProjectIdsKey = visibleTranslationPollingProjectIds.join('|');

  useEffect(() => {
    if (
      !appId ||
      !TRANSLATION_PROGRESS_POLLING_ENABLED ||
      !visibleTranslationPollingProjectIdsKey
    ) {
      return undefined;
    }

    let cancelled = false;
    const visibleProjectIds = visibleTranslationPollingProjectIdsKey.split('|').filter(Boolean);

    const pollProgress = async () => {
      await Promise.all(
        visibleProjectIds.map(async (projectId) => {
          if (unavailableProgressProjectIdsRef.current.has(projectId)) {
            return;
          }

          try {
            const { data } = await coreApi.get<unknown>(
              `/apps/${appId}/projects/${projectId}/translation-progress`,
              {
                skipGlobalErrorToast: true
              }
            );
            if (cancelled) {
              return;
            }

            const progress = normalizeTranslationProgressResponse(data);
            if (!progress) {
              return;
            }

            setTranslationProgressByProjectId((previous) => ({
              ...previous,
              [progress.projectId]: progress
            }));

            if (
              isTerminalTranslationProgress(progress) &&
              !terminalTranslationToastProjectIdsRef.current.has(progress.projectId)
            ) {
              terminalTranslationToastProjectIdsRef.current.add(progress.projectId);
              if (progress.status === 'completed') {
                toast({ message: '번역이 완료됐어요.' });
              } else {
                toast({
                  message: progress.errorMessage ?? '번역에 실패했어요. 다시 시도해주세요.',
                  type: 'error'
                });
              }
              mutateProjects();
              mutateApplicationInfo();
            }
          } catch (error: unknown) {
            const axiosError = error as AxiosError;
            if (
              axiosError.response?.status === 404 ||
              axiosError.response?.status === 405 ||
              axiosError.response?.status === 501
            ) {
              unavailableProgressProjectIdsRef.current.add(projectId);
            }
          }
        })
      );
    };

    pollProgress();
    const intervalId = window.setInterval(pollProgress, TRANSLATION_PROGRESS_POLLING_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    appId,
    mutateApplicationInfo,
    mutateProjects,
    visibleTranslationPollingProjectIdsKey
  ]);

  useEffect(() => {
    if (projects?.length === 0 && isOwner) {
      toast({ message: '어서오세요! 이제 첫번째 게임을 만들어보세요.' });
    }
  }, [projects, isOwner]);

  if (error?.response?.status === 404) {
    const defaultChannelId = localStorage.getItem('defaultChannelID');

    if (appId === defaultChannelId) {
      localStorage.removeItem('defaultChannelID');
    }

    return <Redirect to="/apps" />;
  }

  if (!projects || !projectsWithQuery) return <LoadingSpinner />;

  function checkPermissionForEnterProject<T extends React.MouseEvent | React.KeyboardEvent>(
    event: T
  ) {
    if (!checkLimitAndShowModal('콘텐츠')) {
      event.preventDefault();
      return;
    }

    if (user?.level === 'Tester' && !isAdmin) {
      event.preventDefault();
      openPermissionModal();
      return;
    }
    if (event.target instanceof HTMLAnchorElement) {
      event.target.click();
    }
  }

  function openPermissionModal() {
    openModal({
      // TODO: 배열 타입도 넣을 수 있게 타입 정의 수정하기
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      cssStyle: [TextAlignCenter, ColumnFlex, JustifyCenterFlex],
      title: '현재 등급에서는 접근할 수 없어요',
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      children: (
        <>
          <Icon
            icon="times_circle_regular"
            size="100px"
            color={color.interactive_critical_default}
          />
          <Typography as="p" type="body" cssStyle={[MarginZero, MarginTop10, TextAlignCenter]}>
            접근 권한을 얻기 위해서는 채널 소유자에게 문의해주세요.
          </Typography>
          <Button
            url="https://rwd.to/realworldstudio-manual-channel#d79783942c4e44b9ba1e727ddf4cc5f3"
            external
          >
            등급별 권한 정보 보기
          </Button>
        </>
      )
    });
  }

  const openAiGeneratingAlertModal = () => {
    openModal({
      title: '콘텐츠를 만들고 있어요.',
      primaryAction: {
        content: '닫기',
        onAction: closeModal
      },
      children: (
        <>
          <Typography as="p" type="body">
            AI 만들기는
            <Typography
              as="span"
              type="body"
              cssStyle={css`
                font-weight: 700;
              `}
            >
              {' '}
              약 10분 소요
            </Typography>
            됩니다.
            <br />
            진행 사항은
            <Typography
              as="span"
              type="body"
              cssStyle={css`
                font-weight: 700;
              `}
            >
              {' '}
              새로고침
            </Typography>
            하여 확인할 수 있고, 스토리와 이미지 제작이 완료되면
            <br />
            콘텐츠를 확인할 수 있어요.
          </Typography>
        </>
      )
    });
  };

  const openAiFailedAlertModal = () => {
    openModal({
      title: '콘텐츠 생성에 실패했습니다.',
      primaryAction: {
        content: '닫기',
        onAction: () => {
          closeModal();
        }
      },
      children: (
        <div style={{ textAlign: 'center', paddingTop: '12.33px' }}>
          <div style={{ marginBottom: '18.33px' }}>
            <img src="/assets/icons/exclamation_circle.svg" alt="ai failed icon" />
          </div>
          <Typography as="p" type="body">
            AI 콘텐츠 생성 과정에서 문제가 발생했습니다.
          </Typography>
          <Typography as="p" type="body">
            사용된 크레딧은{' '}
            <Typography
              as="span"
              type="body"
              cssStyle={css`
                font-weight: 700;
              `}
            >
              전부 복구
            </Typography>
            되었습니다.
          </Typography>
          <br />
          <Typography as="p" type="body">
            실패한 콘텐츠는 재생성이 불가하며,
          </Typography>
          <Typography
            as="p"
            type="body"
            cssStyle={css`
              font-weight: 700;
            `}
          >
            복구된 크레딧으로 새로운 콘텐츠를 만들어주세요.
          </Typography>
        </div>
      )
    });
  };

  const openTranslationGeneratingAlertModal = (progress?: TranslationProgressResponseModel) => {
    openModal({
      title: '콘텐츠 번역이 진행 중이에요.',
      primaryAction: {
        content: '닫기',
        onAction: closeModal
      },
      children: (
        <Typography as="p" type="body">
          번역을 자동으로 처리하고 있습니다.
          {progress ? (
            <>
              <br />
              {formatTranslationProgressText(progress)}
            </>
          ) : null}
          <br />
          완료되면 자동으로 목록에 반영됩니다.
        </Typography>
      )
    });
  };

  const openTranslationFailedAlertModal = () => {
    openModal({
      title: '콘텐츠 번역에 실패했습니다.',
      primaryAction: {
        content: '닫기',
        onAction: closeModal
      },
      children: (
        <Typography as="p" type="body">
          번역 작업 중 문제가 발생했습니다.
          <br />
          실패한 콘텐츠는 삭제한 뒤 다시 번역을 요청해주세요.
        </Typography>
      )
    });
  };

  const openCreateContentSelectModal = () => {
    if (!checkLimitAndShowModal('콘텐츠')) {
      return;
    }

    openModal({
      cssStyle: css`
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
      `,
      hasHeader: false,
      hasFooter: false,
      hasCloseIcon: false,
      size: isMobile ? 'small' : 'large',
      isBottomRadius: true,
      children: (
        <CreateProjectTemplateModal
          isInsufficientCredit={
            (creditValues?.data?.freeCredit ?? 0) + (creditValues?.data?.paidCredit ?? 0) < 40
          }
          onClose={closeModal}
          onClickManualCreate={(e) => {
            handleClickCreate('V1', e);
            track.onClick({
              clickEventName: ClickEventName.click_channel_creategame_modal_type,
              params: {
                channel_id: appId,
                type_name: 'general'
              }
            });
          }}
          onClickAiCreate={() => {
            track.onClick({
              clickEventName: ClickEventName.click_channel_creategame_modal_type,
              params: {
                channel_id: appId,
                type_name: 'ai'
              }
            });
            if (
              (creditValues?.data?.freeCredit ?? 0) + (creditValues?.data?.paidCredit ?? 0) <
              40
            ) {
              return;
            }
            history.push(`/apps/${appId}/ai/storyConfig`);
          }}
          onUpgradePlanClick={() => {
            closeModal();
            openPromotionModalHook({
              triggerLocation: PromotionTriggerLocation.ai_credit_insufficient_create
            });
          }}
        />
      )
    });
  };

  const handleClickCreate = async (targetEngine: 'V1' | 'V2', e: React.MouseEvent) => {
    track.onClick({
      clickEventName: ClickEventName.click_channel_button_creategame,
      params: {
        engineVersion: targetEngine
      }
    });
    const isChecked = checkPermissionForCreateProject(e);

    if (!isChecked) {
      return;
    }

    if (targetEngine === 'V1') {
      history.push(`/apps/${appId}/projects/create`);
    } else if (targetEngine === 'V2') {
      history.push(`/apps/${appId}/projectsV2/create`);
    }
  };

  const openDeleteProjectModal = (projectId: string, projectName: string) => {
    if (!appId) {
      return;
    }

    const userLevel = user?.level;

    if (userLevel !== 'Owner') {
      toast({ message: '삭제 권한이 없습니다.', type: 'error' });
      return;
    }

    /**
     * TODO
     *  - ai 삭제 시와 그 외 track 분기 필요
     *  - 디자인 시스템 모달 footer 사용하기
     */

    openModal({
      size: 'medium',
      title: '이 콘텐츠를 삭제하시겠어요?',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <DeleteProjectModal
          projectName={projectName}
          onDelete={() => {
            deleteProject(projectId);
          }}
          onCancel={() => {
            closeModal();
          }}
        />
      )
    });
  };

  const deleteProject = async (projectId: string) => {
    if (isDeletingProject) {
      return; // 중복 요청 방지
    }

    try {
      await mutateByPath(`/apps/${appId}/projects/${projectId}`, 'DELETE');

      // 성공 피드백
      toast({ message: '콘텐츠가 삭제되었습니다', type: 'default' });
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 404) {
        toast({ message: '이미 삭제된 콘텐츠입니다.', type: 'error' });
      } else {
        toast({ message: '콘텐츠 삭제에 실패했어요. 다시 시도해주세요.', type: 'error' });
      }
    } finally {
      await mutateProjects();
      await mutateApplicationInfo();
      closeModal();
    }
  };

  const openDeleteFailedProjectModal = (
    projectId: string,
    projectName: string,
    projectStatus: string
  ) => {
    openModal({
      title: '이 콘텐츠를 삭제하시겠어요?',
      children: '삭제된 이후에는 복구할 수 없어요. 정말 삭제하시겠어요?',
      customActionButton: (
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          <Button
            onClick={() => {
              closeModal();
              track.onClick({
                clickEventName: ClickEventName.click_channel_ai_project_delete_modal_cancel,
                params: {
                  channel_id: appId,
                  project_id: projectId,
                  project_name: projectName,
                  status: projectStatus
                }
              });
            }}
            size="large"
          >
            취소
          </Button>
          <Button
            type="destructive"
            size="large"
            cssStyle={css`
              background-color: #e56767;
              color: ${color.white};
            `}
            onClick={() => {
              deleteProject(projectId);
              track.onClick({
                clickEventName: ClickEventName.click_channel_ai_project_delete_modal_delete,
                params: {
                  channel_id: appId,
                  project_id: projectId,
                  project_name: projectName,
                  status: projectStatus
                }
              });
            }}
          >
            콘텐츠 삭제
          </Button>
        </div>
      )
    });
  };

  const duplicateProject = async (projectId: string) => {
    if (!appId) return;

    if (!canDuplicateProjects) {
      toast({ message: '복제 권한이 없습니다.', type: 'error' });
      return;
    }

    if (hasReachedLimit) {
      openModal({
        title: '콘텐츠를 더 이상 만들 수 없어요',
        children: (
          <Typography as="p" type="body">
            현재 요금제의 콘텐츠 개수 제한에 도달하여 복제할 수 없어요.
            <br />
            요금제를 업그레이드하면 더 많은 콘텐츠를 만들 수 있어요.
          </Typography>
        ),
        primaryAction: {
          content: '요금제 업그레이드',
          onAction: () => {
            closeModal();
            openPromotionModalHook({
              recommendedPlan: plan === 'Free' ? 'Pro' : 'Enterprise',
              triggerLocation: PromotionTriggerLocation.content_create_limit_button
            });
          }
        },
        secondaryAction: {
          content: '닫기',
          onAction: closeModal
        }
      });
      return;
    }

    try {
      const result = (await mutateByPath(
        `/apps/${appId}/projects/${projectId}/duplicate`,
        'POST'
      )) as { ProjectId?: string } | undefined;
      toast({ message: '콘텐츠가 복제되었습니다.' });
      if (result?.ProjectId) {
        setNewProjectId(result.ProjectId);
      }
      await mutateProjects();
      await mutateApplicationInfo();
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message: string }>;
      const serverMessage = axiosError.response?.data?.message;
      toast({
        message: serverMessage ?? '콘텐츠 복제에 실패했어요. 다시 시도해주세요.',
        type: 'error'
      });
    }
  };

  const openTranslateProjectModal = (
    projectId: string,
    projectName: string,
    projectLanguage?: Language
  ) => {
    if (!appId) {
      return;
    }

    openModal({
      size: 'medium',
      title: '콘텐츠 번역',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <TranslateProjectModalContent
          projectName={projectName}
          sourceLanguage={normalizeProjectLanguage(projectLanguage)}
          onCancel={closeModal}
          onSubmit={(targetLanguage) =>
            translateProject(projectId, projectName, projectLanguage, targetLanguage)
          }
        />
      )
    });
  };

  const translateProject = async (
    projectId: string,
    projectName: string,
    projectLanguage: Language | undefined,
    targetLanguage: TranslationTargetLanguage
  ) => {
    if (!appId || translatingProjectId) return false;

    if (!canTranslateProjects) {
      toast({ message: '번역 권한이 없습니다.', type: 'error' });
      return false;
    }

    if (normalizeProjectLanguage(projectLanguage) === targetLanguage) {
      toast({ message: `이미 ${getTranslationLanguageLabel(targetLanguage)}로 설정된 콘텐츠예요.` });
      return false;
    }

    setTranslatingProjectId(projectId);

    try {
      const response = await mutateByPath(`/apps/${appId}/projects/${projectId}/translate`, 'POST', {
        targetLanguage
      });
      const progress = normalizeTranslationProgressResponse(response, targetLanguage);

      if (progress?.projectId) {
        const sourceProject = projects?.find((project) => project.id === projectId);
        const optimisticProject = buildOptimisticTranslationProject(
          sourceProject,
          progress,
          projectName
        );

        setOptimisticTranslationProjects((previous) => [
          optimisticProject,
          ...previous.filter((project) => project.id !== optimisticProject.id)
        ]);
        setTranslationProgressByProjectId((previous) => ({
          ...previous,
          [progress.projectId]: progress
        }));
        setNewProjectId(progress.projectId);
      }

      toast({
        message: `${projectName} ${getTranslationLanguageLabel(
          targetLanguage
        )} 번역을 시작했어요.`
      });
      mutateProjects();
      mutateApplicationInfo();
      return true;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message: string }>;
      const serverMessage = axiosError.response?.data?.message;
      toast({
        message:
          serverMessage ??
          `${getTranslationLanguageLabel(targetLanguage)} 번역에 실패했어요. 다시 시도해주세요.`,
        type: 'error'
      });
      return false;
    } finally {
      setTranslatingProjectId(null);
    }
  };

  const openPromotionModal = () => {
    openPromotionModalHook({
      recommendedPlan: plan === 'Free' ? 'Pro' : 'Enterprise',
      triggerLocation: PromotionTriggerLocation.content_create_limit_button
    });
  };

  return (
    <>
      <div css={ChannelListInfoWrap}>
        <Typography as="h4" type="displaySmall" textColor="initial">
          채널 콘텐츠
        </Typography>
        <Input
          cssStyle={[CardShadowStyle, SearchInputStyle, SearchInputPos]}
          placeholder="콘텐츠 이름으로 검색"
          prefix={<Icon size="20px" icon="search_solid" />}
          onChange={(value) => {
            setSearchQuery(value);
            track.onInputThrottled({
              inputEventName: InputEventName.input_channel_searchgames
            });
          }}
          value={searchQuery}
        />
      </div>
      <section css={MediaCardContainerStyle}>
        <>
          {hasReachedLimit && !isAdmin ? (
            <DisableCreateProject onClick={openPromotionModal} currentPlan={plan} />
          ) : (
            <CreateProject onClick={openCreateContentSelectModal} />
          )}
          {projectsWithQuery?.map((project) => {
            const isTranslationStatusProject = isTranslationProject(project);
            const translationProgress =
              project.translationProgress ?? translationProgressByProjectId[project.id];

            if (project.status === 'Creating') {
              return (
                <div
                  key={project.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!checkLimitAndShowModal('콘텐츠')) {
                      return;
                    }
                    track.onClick({
                      clickEventName: ClickEventName.click_channel_ai_creating_project,
                      params: {
                        channel_id: appId,
                        project_id: project.id,
                        project_name: project.name
                      }
                    });
                    if (isTranslationStatusProject) {
                      openTranslationGeneratingAlertModal(translationProgress);
                    } else {
                      openAiGeneratingAlertModal();
                    }
                  }}
                  onKeyDown={keyPressHandler(() => {
                    if (isTranslationStatusProject) {
                      openTranslationGeneratingAlertModal(translationProgress);
                    } else {
                      openAiGeneratingAlertModal();
                    }
                  })}
                >
                  {isTranslationStatusProject ? (
                    <TranslationProgressThumbnail
                      title={project.name}
                      progress={translationProgress}
                    />
                  ) : (
                    <AiLoadingThumbnail title={project.name} />
                  )}
                </div>
              );
            }

            if (project.status === 'Failed') {
              return (
                <MediaCard
                  key={project.id}
                  onKeyDown={keyPressHandler(checkPermissionForEnterProject)}
                  onClick={() => {
                    if (!checkLimitAndShowModal('콘텐츠')) {
                      return;
                    }
                    track.onClick({
                      clickEventName: ClickEventName.click_channel_ai_failed_project,
                      params: {
                        channel_id: appId,
                        project_id: project.id,
                        project_name: project.name
                      }
                    });
                    if (isTranslationStatusProject) {
                      openTranslationFailedAlertModal();
                    } else {
                      openAiFailedAlertModal();
                    }
                  }}
                  badge={
                    <div css={FailedBadgeStyle}>
                      {isTranslationStatusProject ? '번역 실패' : '생성 실패'}
                    </div>
                  }
                  source={project.horizontalImageUrl}
                  title={isTranslationStatusProject ? '콘텐츠 번역 실패' : '콘텐츠 생성 실패'}
                  loadingThumbnail={<AIFailedTumbnail />}
                  summary={
                    isTranslationStatusProject
                      ? '번역 작업 중 문제가 발생했어요.'
                      : '사용된 크레딧은 전부 복구되었어요.'
                  }
                  isMoreAction
                  moreActionIcon={
                    <ProjectActionsMenu
                      projectId={project.id}
                      projectName={project.name}
                      projectStatus={project.status}
                      onDelete={openDeleteFailedProjectModal}
                      isFailedProject
                    />
                  }
                />
              );
            }

            const isNewProject = project.id === newProjectId;

            return (
              <div
                key={project.id}
                ref={isNewProject ? newProjectRef : undefined}
                css={isNewProject ? NewProjectHighlightStyle : undefined}
              >
                <MediaCard
                  linkURL={
                    project.projectEngineVersion === 'V2'
                      ? `#`
                      : `/apps/${appId}/projects/${project.id}/detail`
                  }
                  onKeyDown={keyPressHandler(checkPermissionForEnterProject)}
                  onClick={
                    project.projectEngineVersion === 'V2'
                      ? async () => {
                          if (!checkLimitAndShowModal('콘텐츠')) {
                            return;
                          }
                          await authApiWithToken.apiAuthLoginPost();
                          window.location.href = `https://${STATION_URL}/${appId}/game/${project.id}`;
                        }
                      : checkPermissionForEnterProject
                  }
                  badge={
                    project.projectEngineVersion === 'V2' ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: 4,
                          alignItems: 'top',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Badge {...badgeProps[project.status as keyof typeof badgeProps]} />
                        <div css={v2IconWrap}>
                          <Icon icon="play_flow_solid" size="24px" color="#fff" />
                        </div>
                      </div>
                    ) : (
                      <Badge {...badgeProps[project.status as keyof typeof badgeProps]} />
                    )
                  }
                  source={project.horizontalImageUrl}
                  title={project.name}
                  summary={project.description ?? ''}
                  isMoreAction
                  moreActionIcon={
                    <ProjectActionsMenu
                      projectId={project.id}
                      projectName={project.name}
                      projectStatus={project.status}
                      onDelete={openDeleteProjectModal}
                      onDuplicate={canDuplicateProjects ? duplicateProject : undefined}
                      onTranslate={canTranslateProjects ? openTranslateProjectModal : undefined}
                      projectLanguage={project.language}
                      isTranslating={translatingProjectId === project.id}
                    />
                  }
                />
              </div>
            );
          })}
        </>
      </section>
      <Modal {...modal} />
      <Modal {...limitModal} />
    </>
  );
}

export default memo(ProjectList);

const SearchInputPos = css`
  margin-left: auto;

  ${onMobile} {
    margin-left: initial;
    margin-top: ${spacing.margin.large};
  }
`;

const SearchInputStyle = css`
  min-width: 300px;

  ${onMobile} {
    min-width: 1px;
  }
`;

const ChannelListInfoWrap = css`
  ${AlignCenterFlex};
  margin-bottom: 36px;
  margin-top: 32px;

  ${onMobile} {
    ${ColumnFlex};

    margin-bottom: 20px;
    margin-top: 24px;
  }
`;

const MediaCardContainerStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: minmax(min-content, 340px);

  @supports (aspect-ratio: 310 / 340) {
    grid-auto-rows: initial;
    > a,
    > div,
    button {
      aspect-ratio: 310 / 340;
    }

    > div > a {
      height: 100%;
    }
  }

  gap: ${spacing.margin.large};

  > a,
  > div {
    && {
      box-shadow: none;
      transition: box-shadow 0.2s ease;
    }

    &:hover {
      && {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    }
  }

  ${onMobile} {
    display: block;

    > a,
    button {
      aspect-ratio: initial;
    }

    > button,
    *:not(:last-of-type) {
      margin-bottom: ${spacing.margin.large};
    }
  }
`;

const v2IconWrap = css`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${color.interactive_primary_default};
`;

const FailedBadgeStyle = css`
  z-index: 1;
  position: relative;
  display: inline-flex;
  height: 24.387px;
  padding: 4px 12px;
  align-items: center;
  gap: 4px;
  color: white;
  border-radius: 100px;
  background: var(--action-Critical-Default, #e56767) !important;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.12);
  color: var(--text-text-on-color, #fff);
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;

  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const NewProjectHighlightStyle = css`
  @keyframes highlightFadeIn {
    0% {
      opacity: 0;
      transform: scale(0.95);
    }
    50% {
      box-shadow: 0 0 0 3px ${color.interactive_primary_default};
      opacity: 1;
      transform: scale(1);
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
      opacity: 1;
      transform: scale(1);
    }
  }

  border-radius: ${spacing.borderRadius.xlarge};
  animation: highlightFadeIn 1.5s ease-out;
`;

function canDuplicateProject(level?: string, isAdmin?: boolean) {
  return isAdmin || level === 'Owner' || level === 'Author' || level === 'Developer';
}

const TRANSLATION_PROGRESS_POLLING_ENABLED =
  import.meta.env.VITE_PROJECT_TRANSLATION_PROGRESS_ENABLED !== 'false';
const TRANSLATION_PROGRESS_POLLING_INTERVAL_MS = 5000;

const TRANSLATE_TARGET_LANGUAGE_OPTIONS: Array<{
  value: TranslationTargetLanguage;
  label: string;
  beta?: boolean;
}> = [
  { value: 'English', label: '영어' },
  { value: 'Japanese', label: '일본어', beta: true },
  { value: 'Chinese', label: '중국어', beta: true }
];

const TRANSLATION_ALLOWED_APP_IDS = new Set([
  'RealWorld',
  '6_6fmTlL_i90KNooYd4AIg',
  '_Z9bqXKmpuDn9jMPH0tYmQ',
  'qeiYlrIFpcNYm7_j4f2o9A',
  'kwfHN_h5j_6mjqq7GyRGdQ',
  '6wBFb8N4DFCCLHGHINryZw'
]);

const TRANSLATED_PROJECT_NAME_PREFIXES = [
  '[한국어]',
  '[English]',
  '[日本語]',
  '[中文简体]',
  '[中文繁體]'
];

function normalizeProjectLanguage(language?: Language): Language {
  return language ?? 'Korean';
}

function getTranslationLanguageLabel(language: TranslationTargetLanguage) {
  return TRANSLATE_TARGET_LANGUAGE_OPTIONS.find((option) => option.value === language)?.label ?? language;
}

function canUseProjectTranslation(
  appId: string | undefined,
  isAdmin?: boolean,
  level?: string,
  email?: string,
  userType?: string | number
) {
  if (isAdmin || isUniquegoodAdmin(email, userType)) return true;
  if (!appId || !TRANSLATION_ALLOWED_APP_IDS.has(appId)) return false;
  return level === 'Owner' || level === 'Author' || level === 'Developer';
}

function isUniquegoodAdmin(email?: string, userType?: string | number) {
  const normalizedUserType = String(userType ?? '')
    .trim()
    .toLowerCase();
  const normalizedEmail = email?.trim().toLowerCase() ?? '';

  return normalizedUserType === 'admin' && normalizedEmail.endsWith('@uniquegood.biz');
}

function isTranslationProject(project: Project) {
  const description = project.description ?? '';
  const name = project.name ?? '';
  return (
    description.includes('번역') ||
    TRANSLATED_PROJECT_NAME_PREFIXES.some((prefix) => name.startsWith(prefix))
  );
}

function isActiveTranslationProgress(progress?: TranslationProgressResponseModel) {
  return progress?.status === 'queued' || progress?.status === 'processing' || progress?.status === 'retrying';
}

function isTerminalTranslationProgress(progress: TranslationProgressResponseModel) {
  return progress.status === 'completed' || progress.status === 'failed';
}

function formatTranslationProgressText(progress: TranslationProgressResponseModel) {
  const chunkText =
    progress.chunkCount > 0
      ? `${progress.completedChunkCount}/${progress.chunkCount} 단계`
      : '진행률 계산 중';
  const fieldText =
    progress.totalFieldCount > 0
      ? ` · ${progress.completedFieldCount}/${progress.totalFieldCount} 항목`
      : '';
  return `진행률 ${progress.progressPercent}% · ${chunkText}${fieldText}`;
}

function normalizeTranslationProgressResponse(
  response: unknown,
  fallbackTargetLanguage: TranslationTargetLanguage = 'English'
): TranslationProgressResponseModel | null {
  const root = getResponseRoot(response);
  if (!root) {
    return null;
  }

  const projectId = getStringValue(root, 'projectId', 'ProjectId', 'id', 'Id');
  if (!projectId) {
    return null;
  }

  const targetLanguage = normalizeTranslationTargetLanguage(
    getStringValue(root, 'targetLanguage', 'TargetLanguage'),
    fallbackTargetLanguage
  );
  const completedChunkCount = getNumberValue(root, 'completedChunkCount', 'CompletedChunkCount') ?? 0;
  const chunkCount = getNumberValue(root, 'chunkCount', 'ChunkCount') ?? 0;
  const completedFieldCount = getNumberValue(root, 'completedFieldCount', 'CompletedFieldCount') ?? 0;
  const totalFieldCount = getNumberValue(root, 'totalFieldCount', 'TotalFieldCount') ?? 0;
  const progressPercent =
    getNumberValue(root, 'progressPercent', 'ProgressPercent') ??
    (chunkCount > 0 ? Math.floor((completedChunkCount / chunkCount) * 100) : 0);

  return {
    projectId,
    projectName:
      getStringValue(root, 'projectName', 'ProjectName', 'name', 'Name') ??
      buildTranslatedProjectName('', targetLanguage),
    sourceProjectId: getStringValue(root, 'sourceProjectId', 'SourceProjectId'),
    targetLanguage,
    jobId: getStringValue(root, 'jobId', 'JobId'),
    status: normalizeTranslationProgressStatus(getStringValue(root, 'status', 'Status')),
    stage: normalizeTranslationProgressStage(getStringValue(root, 'stage', 'Stage')),
    completedFieldCount,
    totalFieldCount,
    completedChunkCount,
    chunkCount,
    progressPercent: clampProgressPercent(progressPercent),
    translatedTextCount: getNumberValue(root, 'translatedTextCount', 'TranslatedTextCount'),
    requestCount: getNumberValue(root, 'requestCount', 'RequestCount'),
    retryCount: getNumberValue(root, 'retryCount', 'RetryCount'),
    inputTokens: getNumberValue(root, 'inputTokens', 'InputTokens'),
    outputTokens: getNumberValue(root, 'outputTokens', 'OutputTokens'),
    totalTokens: getNumberValue(root, 'totalTokens', 'TotalTokens'),
    updatedAt: getStringValue(root, 'updatedAt', 'UpdatedAt'),
    errorMessage: getStringValue(root, 'errorMessage', 'ErrorMessage', 'error', 'Error') ?? null
  };
}

function getResponseRoot(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const record = response as Record<string, unknown>;
  if (record.data && typeof record.data === 'object') {
    return record.data as Record<string, unknown>;
  }

  return record;
}

function getStringValue(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return undefined;
}

function getNumberValue(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return undefined;
}

function normalizeTranslationTargetLanguage(
  value: string | undefined,
  fallback: TranslationTargetLanguage
): TranslationTargetLanguage {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'japanese' || normalized === 'ja') return 'Japanese';
  if (normalized === 'chinese' || normalized === 'zh' || normalized === 'zh-cn') return 'Chinese';
  if (normalized === 'english' || normalized === 'en') return 'English';
  return fallback;
}

function normalizeTranslationProgressStatus(value?: string): TranslationProgressStatus {
  switch (value?.trim().toLowerCase()) {
    case 'completed':
    case 'complete':
    case 'unreleased':
    case 'released':
      return 'completed';
    case 'failed':
    case 'fail':
      return 'failed';
    case 'queued':
    case 'queue':
      return 'queued';
    case 'retrying':
      return 'retrying';
    case 'processing':
    case 'creating':
    default:
      return 'processing';
  }
}

function normalizeTranslationProgressStage(value?: string): TranslationProgressStage {
  switch (value?.trim().toLowerCase()) {
    case 'cloning':
      return 'cloning';
    case 'queued':
      return 'queued';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'translating':
    default:
      return 'translating';
  }
}

function clampProgressPercent(value: number) {
  return Math.min(Math.max(Math.floor(value), 0), 100);
}

function buildTranslatedProjectName(sourceName: string, targetLanguage: TranslationTargetLanguage) {
  const prefix =
    {
      English: '[English]',
      Japanese: '[日本語]',
      Chinese: '[中文简体]'
    }[targetLanguage] ?? '[Translated]';

  return `${prefix} ${sourceName}`.trim();
}

function buildOptimisticTranslationProject(
  sourceProject: Project | undefined,
  progress: TranslationProgressResponseModel,
  fallbackSourceProjectName: string
): ProjectWithTranslationProgress {
  let targetStatus: Project['status'] = 'Creating';
  if (progress.status === 'failed') {
    targetStatus = 'Failed';
  }
  if (progress.status === 'completed') {
    targetStatus = 'Unreleased';
  }

  return {
    ...(sourceProject as Project),
    id: progress.projectId,
    name:
      progress.projectName ||
      buildTranslatedProjectName(fallbackSourceProjectName, progress.targetLanguage),
    description: sourceProject?.description ?? '번역 진행 중',
    status: targetStatus,
    language: progress.targetLanguage,
    preparationMessage: sourceProject?.preparationMessage ?? '',
    isPreparing: false,
    translationProgress: progress,
    isOptimisticTranslation: true
  };
}

function TranslateProjectModalContent({
  projectName,
  sourceLanguage,
  onCancel,
  onSubmit
}: {
  projectName: string;
  sourceLanguage: Language;
  onCancel: () => void;
  onSubmit: (targetLanguage: TranslationTargetLanguage) => Promise<boolean>;
}) {
  const [selectedLanguage, setSelectedLanguage] = useState<TranslationTargetLanguage | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async () => {
    if (!selectedLanguage || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const didStart = await onSubmit(selectedLanguage);
    setIsSubmitting(false);

    if (didStart) {
      onCancel();
    }
  };

  return (
    <div css={TranslateModalContentStyle}>
      <Typography as="p" type="body" textColor="subdued">
        {projectName}을 선택한 언어로 번역합니다.
      </Typography>
      <div css={TranslateLanguageListStyle}>
        {TRANSLATE_TARGET_LANGUAGE_OPTIONS.map((option) => {
          const isSelected = selectedLanguage === option.value;
          const isSourceLanguage = sourceLanguage === option.value;

          return (
            <button
              key={option.value}
              type="button"
              css={TranslateLanguageButtonStyle(isSelected, isSourceLanguage)}
              disabled={isSourceLanguage || isSubmitting}
              onClick={() => setSelectedLanguage(option.value)}
            >
              <span>{option.label}</span>
              {option.beta ? <span css={BetaBadgeStyle}>베타</span> : null}
            </button>
          );
        })}
      </div>
      <div css={TranslateModalActionsStyle}>
        <Button type="basic" size="large" onClick={onCancel} disabled={isSubmitting}>
          취소
        </Button>
        <Button
          size="large"
          onClick={submit}
          disabled={!selectedLanguage || isSubmitting}
          loading={isSubmitting}
        >
          번역 시작
        </Button>
      </div>
    </div>
  );
}

function TranslationProgressThumbnail({
  title,
  progress
}: {
  title?: string;
  progress?: TranslationProgressResponseModel;
}) {
  const progressPercent = progress?.progressPercent ?? 0;
  const chunkText =
    progress && progress.chunkCount > 0
      ? `${progress.completedChunkCount}/${progress.chunkCount} 단계`
      : '진행률 계산 중';
  const fieldText =
    progress && progress.totalFieldCount > 0
      ? `${progress.completedFieldCount}/${progress.totalFieldCount} 항목`
      : '';

  return (
    <div css={TranslationThumbnailContainerStyle}>
      <div css={TranslationBadgeStyle}>번역 진행 중</div>
      <div css={TranslationCardContentStyle}>
        <div css={TranslationTitleStyle}>{title}</div>
        <div css={TranslationProgressValueStyle}>{progressPercent}%</div>
        <div css={TranslationProgressBarStyle}>
          <div css={TranslationProgressFillStyle(progressPercent)} />
        </div>
        <div css={TranslationProgressMetaStyle}>
          {chunkText}
          {fieldText ? ` · ${fieldText}` : ''}
        </div>
      </div>
    </div>
  );
}

const TranslateModalContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.margin.large};
`;

const TranslateLanguageListStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${spacing.margin.small};

  ${onMobile} {
    grid-template-columns: 1fr;
  }
`;

const TranslateLanguageButtonStyle = (isSelected: boolean, isDisabled: boolean) => css`
  min-height: 56px;
  border: 1px solid
    ${isSelected ? color.interactive_primary_default : color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  background: ${isSelected ? '#f2f9ff' : color.surface_default_default};
  color: ${isDisabled ? color.text_disabled : color.text_default};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.margin.xsmall};
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  opacity: ${isDisabled ? 0.5 : 1};
`;

const BetaBadgeStyle = css`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 100px;
  background: #edf2ff;
  color: #4a41ff;
  font-size: 11px;
  font-weight: 700;
`;

const TranslateModalActionsStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.margin.small};
`;

const TranslationThumbnailContainerStyle = css`
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 310 / 330;
  background-color: #f2f9ff;
  border-radius: 16px;
  border: 1px solid var(--border-Default-Subdued, #ededed);
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;

  ${onMobile} {
    height: 160px;
  }
`;

const TranslationBadgeStyle = css`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1;
  display: inline-flex;
  height: 24px;
  padding: 4px 12px;
  align-items: center;
  border-radius: 100px;
  background: ${color.interactive_primary_default};
  color: ${color.white};
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

const TranslationCardContentStyle = css`
  display: flex;
  width: 100%;
  max-width: 240px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const TranslationTitleStyle = css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  color: var(--text-Default, #191919);
  text-overflow: ellipsis;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
`;

const TranslationProgressValueStyle = css`
  color: ${color.interactive_primary_default};
  font-family: Pretendard;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
`;

const TranslationProgressBarStyle = css`
  width: 100%;
  height: 6px;
  border-radius: 100px;
  overflow: hidden;
  background: #dce7f6;
`;

const TranslationProgressFillStyle = (progressPercent: number) => css`
  width: ${clampProgressPercent(progressPercent)}%;
  height: 100%;
  border-radius: 100px;
  background: ${color.interactive_primary_default};
  transition: width 0.3s ease;
`;

const TranslationProgressMetaStyle = css`
  color: ${color.text_subdued};
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;

const ProjectActionsPopoverStyle = css`
  width: 150px;
  margin-top: ${spacing.margin.xsmall};

  > .Content {
    border-radius: ${spacing.borderRadius.medium};
    border: 1px solid ${color.border_default_subdued};
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

interface ProjectActionsMenuProps {
  projectId: string;
  projectName: string;
  projectLanguage?: Language;
  projectStatus: string;
  onDelete: (projectId: string, projectName: string, projectStatus: string) => void;
  onDuplicate?: (projectId: string, projectName: string) => void;
  onTranslate?: (projectId: string, projectName: string, projectLanguage?: Language) => void;
  isTranslating?: boolean;
  isFailedProject?: boolean;
}

function ProjectActionsMenu({
  projectId,
  projectName,
  projectLanguage,
  projectStatus,
  onDelete,
  onDuplicate,
  onTranslate,
  isTranslating = false,
  isFailedProject = false
}: ProjectActionsMenuProps) {
  const { appId } = useParams<AppParam>();
  const [active, setActive] = useState(false);

  const handleDelete = () => {
    onDelete(projectId, projectName, projectStatus);

    track.onClick({
      clickEventName: ClickEventName.click_channel_ai_project_delete,
      params: {
        channel_id: appId,
        project_id: projectId,
        project_name: projectName,
        status: projectStatus
      }
    });
  };

  const handleDuplicate = () => {
    setActive(false);
    onDuplicate?.(projectId, projectName);
  };

  const handleTranslate = () => {
    setActive(false);
    onTranslate?.(projectId, projectName, projectLanguage);
  };

  if (isFailedProject) {
    return (
      <Button
        cssStyle={css`
          border-radius: ${spacing.borderRadius.medium};
          width: 24px;
          height: 24px;
          padding: 0;
          svg {
            width: 16px;
            height: 16px;
          }
        `}
        type="basic"
        icon="trash_alt_solid"
        size="small"
        onClick={handleDelete}
      />
    );
  }

  return (
    <Popover
      active={active}
      preferredAlignment="left"
      activator={
        <Button
          cssStyle={css`
            border-radius: ${spacing.borderRadius.medium};
            width: 24px;
            height: 24px;
            padding: 0;
            svg {
              width: 16px;
              height: 16px;
            }
          `}
          type="plain"
          icon="ellipsis_v_regular"
          size="small"
          onClick={() => setActive(!active)}
        />
      }
      onClose={() => setActive(false)}
      contentCssStyle={ProjectActionsPopoverStyle}
    >
      <ActionList
        items={[
          ...(onDuplicate
            ? [
                {
                  content: '콘텐츠 복제하기',
                  onAction: handleDuplicate
                }
              ]
            : []),
          ...(onTranslate
            ? [
                {
                  content: isTranslating ? '번역 준비 중...' : '번역',
                  onAction: handleTranslate
                }
              ]
            : []),
          {
            content: '콘텐츠 삭제하기',
            destructive: true,
            onAction: handleDelete
          }
        ]}
      />
    </Popover>
  );
}
